import styled from "@emotion/styled";
import { Col, Input, message, Modal, Row } from "antd";
import Button from "atoms/Button";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { useEffect, useState } from "react";
import {
  allowNumberKeys,
  ENCASHMENT_STATUS,
  EncashmentStatusTag,
  formatDate,
} from "./common";

const StyledModal = styled(Modal)`
  .ant-modal-title {
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 10px;
    .ant-tag {
      font-weight: normal;
    }
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-input-number {
    width: 100%;
  }
  .summary {
    display: inline-block;
    padding: 4px 12px;
    margin-bottom: 16px;
    border-radius: 16px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    font-size: 13px;
  }
  .label {
    color: #6b7280;
    margin-bottom: 4px;
  }
  .value {
    margin-bottom: 16px;
    word-break: break-word;
  }
  .section-title {
    font-weight: 600;
    margin: 8px 0 12px;
  }
  .warning {
    color: #d4380d;
  }
  .hint {
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
  }
  .footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 20px;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 5px;
  min-width: 110px;
  margin-left: 0;
`;

const REJECT_REASON_MAX_LENGTH = 250;

const Field = ({ label, children }) => (
  <>
    <div className="label">{label}</div>
    <div className="value">{children || "-"}</div>
  </>
);

function LeaveEncashmentRequestModal({
  isModalVisible,
  closeModal,
  encashment,
  ClientId,
  canApprove,
  onActionComplete,
}) {
  // Text value of "Days to Approve"; the field is disabled, so it stays the requested days.
  const [daysToApprove, setDaysToApprove] = useState("");
  const [showRejectModal, toggleRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [loadingAction, setLoadingAction] = useState("");

  const requestedDays = Number(encashment?.number_of_days) || 0;
  const currentBalance = Number(
    encashment?.current_balance ?? encashment?.available_balance ?? 0
  );
  const isWaiting = encashment?.status_id === ENCASHMENT_STATUS.WAITING;
  const isActionable = canApprove && isWaiting;
  const approvedDays = isActionable
    ? daysToApprove
    : String(encashment?.approved_days ?? encashment?.number_of_days ?? "");
  const daysToApproveNumber = Number(daysToApprove);

  useEffect(() => {
    if (isModalVisible) {
      setDaysToApprove(requestedDays > 0 ? String(requestedDays) : "");
      setRejectReason("");
      toggleRejectModal(false);
    }
  }, [isModalVisible, encashment]);

  const updateStatus = async (statusId, declineReason = "") => {
    setLoadingAction(statusId);
    const { code } = await CALL_API(
      `approve-reject-leave-encashment/${ClientId}`,
      "post",
      {
        encashment_id: encashment?.encashment_id,
        userid: encashment?.userid,
        status_id: statusId,
        approved_days:
          statusId === ENCASHMENT_STATUS.APPROVED ? daysToApproveNumber : 0,
        decline_reason: declineReason,
      }
    );
    setLoadingAction("");
    if (code === STATUS_CODE.SUCCESS) {
      message.success(
        statusId === ENCASHMENT_STATUS.APPROVED
          ? "Leave encashment has been approved"
          : "Leave encashment has been rejected"
      );
      toggleRejectModal(false);
      closeModal();
      onActionComplete && onActionComplete();
    } else if (code === STATUS_CODE.LEAVE_EXCEED) {
      message.error("Days to approve exceed the employee's current balance");
    } else if (code === STATUS_CODE.RECORD_EXIST) {
      message.error("This request has already been approved or rejected");
      closeModal();
      onActionComplete && onActionComplete();
    } else {
      message.error("Something went wrong. Please try again.");
    }
  };

  const exceedsBalance = daysToApproveNumber > currentBalance;
  const isValidDays =
    daysToApproveNumber > 0 &&
    daysToApproveNumber <= requestedDays &&
    !exceedsBalance;

  return (
    <>
      <StyledModal
        title={
          <>
            Leave Encashment Request
            <EncashmentStatusTag statusId={encashment?.status_id} />
          </>
        }
        open={isModalVisible && !showRejectModal}
        onCancel={closeModal}
        width={560}
        footer={[]}
      >
        <div className="summary">
          Current balance: <strong>{currentBalance} day(s)</strong> &bull;
          Requested: <strong>{requestedDays} day(s)</strong>
        </div>
        <Row gutter={20}>
          <Col xs={24} sm={12}>
            <Field label="Employee">{encashment?.fullname}</Field>
          </Col>
          <Col xs={24} sm={12}>
            <Field label="Leave Type">{encashment?.leave_category_name}</Field>
          </Col>
          <Col xs={24} sm={12}>
            <Field label="Submitted by">
              {encashment?.submitted_by_name || encashment?.fullname}
            </Field>
          </Col>
          <Col xs={24} sm={12}>
            <Field label="Submitted on">{formatDate(encashment?.created_at)}</Field>
          </Col>
          <Col xs={24}>
            <Field label="Reason">{encashment?.reason}</Field>
          </Col>
          {encashment?.status_id === ENCASHMENT_STATUS.REJECTED && (
            <Col xs={24}>
              <Field label="Reason for Rejection">{encashment?.decline_reason}</Field>
            </Col>
          )}
          {encashment?.approved_rejected_by_name && (
            <Col xs={24}>
              <Field
                label={
                  encashment?.status_id === ENCASHMENT_STATUS.REJECTED
                    ? "Rejected by"
                    : "Approved by"
                }
              >
                {encashment?.approved_rejected_by_name}
              </Field>
            </Col>
          )}
        </Row>

        {encashment?.status_id !== ENCASHMENT_STATUS.REJECTED && (
          <>
            <div className="section-title">Approval</div>
            <div className="label">Days to Approve</div>
            <Input
              type="text"
              inputMode="decimal"
              value={approvedDays}
              disabled
              onKeyDown={allowNumberKeys}
              onChange={(e) => setDaysToApprove(e.target.value)}
            />
            {isActionable && (
              <div className="hint">
                {exceedsBalance ? (
                  <span className="warning">
                    Current balance is lower than the requested days, so this
                    request can only be rejected.
                  </span>
                ) : (
                  <>
                    Balance after approval:{" "}
                    <strong>{currentBalance - daysToApproveNumber} days</strong>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {isActionable && (
          <div className="footer">
            <StyledButton
              onClick={() => toggleRejectModal(true)}
              disabled={!!loadingAction}
            >
              Reject
            </StyledButton>
            <StyledButton
              type="primary"
              loading={loadingAction === ENCASHMENT_STATUS.APPROVED}
              disabled={!isValidDays || !!loadingAction}
              onClick={() => updateStatus(ENCASHMENT_STATUS.APPROVED)}
            >
              Approve
            </StyledButton>
          </div>
        )}
      </StyledModal>

      <StyledModal
        title={null}
        open={isModalVisible && showRejectModal}
        onCancel={() => toggleRejectModal(false)}
        closable={false}
        width={560}
        footer={[]}
      >
        <div className="label">Reason for Rejection</div>
        <Input.TextArea
          value={rejectReason}
          maxLength={REJECT_REASON_MAX_LENGTH}
          autoSize={{ minRows: 3, maxRows: 6 }}
          onChange={(e) => setRejectReason(e.target.value)}
        />
        <div className="hint">Maximum {REJECT_REASON_MAX_LENGTH} characters</div>
        <div className="footer">
          <StyledButton
            onClick={() => toggleRejectModal(false)}
            disabled={!!loadingAction}
          >
            Cancel
          </StyledButton>
          <StyledButton
            type="primary"
            loading={loadingAction === ENCASHMENT_STATUS.REJECTED}
            disabled={!rejectReason.trim()}
            onClick={() =>
              updateStatus(ENCASHMENT_STATUS.REJECTED, rejectReason.trim())
            }
          >
            Reject
          </StyledButton>
        </div>
      </StyledModal>
    </>
  );
}

export default LeaveEncashmentRequestModal;
